import type { Component } from 'grapesjs';
import type { IClipboardData, IStyleData, IIdMapping, IGrapesJSRule } from './types';

/**
 * Генерирует уникальную сигнатуру правила для избежания дубликатов.
 * Комбинирует селектор, состояние, медиазапрос и тип at-rule.
 */
export function generateRuleSignature(
  selector: string,
  state?: string,
  mediaText?: string,
  atRuleType?: string,
): string {
  return `${selector}|${state || ''}|${mediaText || ''}|${atRuleType || ''}`;
}

/**
 * Группирует стили по медиазапросам для оптимизации применения.
 * Правила с одинаковыми медиазапросами обрабатываются вместе.
 */
export function groupStylesByMedia(styles: Record<string, unknown>[]): Record<string, Record<string, unknown>[]> {
  const grouped: Record<string, Record<string, unknown>[]> = {};

  styles.forEach(style => {
    let key: string;

    if (style.atRuleType && style.mediaText) {
      // Медиазапрос: 'media:(max-width: 768px)'
      key = `${style.atRuleType}:${style.mediaText}`;
    } else if (style.atRuleType) {
      // Только at-rule без медиазапроса: 'keyframes'
      key = style.atRuleType as string;
    } else {
      // Обычные стили без медиазапросов: 'default'
      key = 'default';
    }

    if (!grouped[key]) {
      grouped[key] = [];
    }
    grouped[key].push(style);
  });

  return grouped;
}

/**
 * Проверяет применимость CSS правила к компоненту.
 * Учитывает ID селекторы, классовые селекторы и состояния.
 */
export function isRuleApplicable(
  selectorString: string,
  ruleState: string | undefined,
  componentId: string | undefined,
  componentClasses: string[],
): boolean {
  // Проверяем ID селекторы
  if (componentId) {
    // Проверяем точное совпадение с ID (с состоянием или без)
    if (selectorString === `#${componentId}` || (ruleState && selectorString === `#${componentId}:${ruleState}`)) {
      return true;
    }

    // Проверяем, начинается ли селектор с ID и содержит состояние
    // Это покрывает случаи, когда selectorString уже содержит состояние
    if (selectorString.startsWith(`#${componentId}:`)) {
      return true;
    }
  }

  // Проверяем классовые селекторы
  if (componentClasses.length > 0) {
    const expectedClassSelector = '.' + componentClasses.join('.');

    if (
      selectorString === expectedClassSelector ||
      (ruleState && selectorString === `${expectedClassSelector}:${ruleState}`)
    ) {
      return true;
    }

    // Проверяем одиночные классы
    for (const className of componentClasses) {
      if (selectorString === `.${className}` || (ruleState && selectorString === `.${className}:${ruleState}`)) {
        return true;
      }

      // Проверяем, начинается ли селектор с класса и содержит состояние
      if (selectorString.startsWith(`.${className}:`)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Собирает стили для компонента и его дочерних элементов.
 * Использует рекурсивный подход для обхода всех компонентов.
 */
export function collectComponentStyles(
  component: Component,
  allRules: IGrapesJSRule[],
  processedRuleSignatures: Set<string>,
): IStyleData[] {
  const styles: IStyleData[] = [];
  const componentId = component.getId();
  const componentClasses = component.getClasses();

  allRules.forEach(rule => {
    const selectorString = rule.selectorsToString ? rule.selectorsToString() : '';
    const ruleState = rule.get('state');

    if (isRuleApplicable(selectorString, ruleState as string | undefined, componentId, componentClasses)) {
      const ruleJson = rule.toJSON();
      const signature = generateRuleSignature(
        selectorString,
        ruleJson.state as string | undefined,
        ruleJson.mediaText as string | undefined,
        ruleJson.atRuleType as string | undefined,
      );

      const hasStyles = ruleJson.style && Object.keys(ruleJson.style).length > 0;
      const notProcessed = !processedRuleSignatures.has(signature);

      if (hasStyles && notProcessed) {
        processedRuleSignatures.add(signature);

        // ВАЖНО: Используем selectorString вместо ruleJson.selectors,
        // так как ruleJson.selectors содержит объекты Selector, а не строки!
        // selectorString уже содержит полный селектор с состоянием (например, '#iymg:hover')
        const styleData = {
          selectors: [selectorString], // Используем полный селектор из selectorsToString()
          style: ruleJson.style as Record<string, unknown>,
          state: ruleJson.state as string | undefined,
          mediaText: ruleJson.mediaText as string | undefined,
          atRuleType: ruleJson.atRuleType as string | undefined,
          selectorsAdd: ruleJson.selectorsAdd as string | undefined,
          important: ruleJson.important as boolean | string[] | undefined,
          group: ruleJson.group as string | undefined,
        };

        styles.push(styleData);
      }
    }
  });

  // Рекурсивно обрабатываем дочерние элементы
  component.components().forEach(child => {
    styles.push(...collectComponentStyles(child, allRules, processedRuleSignatures));
  });

  return styles;
}

/**
 * Строит маппинг старых ID на новые для компонента и его дочерних элементов.
 * Рекурсивно обрабатывает всю иерархию компонентов.
 */
export function buildIdMapping(
  originalData: Record<string, unknown>,
  clonedComponent: Component,
  idMapping: IIdMapping = {},
): IIdMapping {
  const oldId = (originalData.attributes as Record<string, unknown>)?.id as string;
  const newId = clonedComponent.getId();

  if (oldId && newId && oldId !== newId) {
    idMapping[oldId] = newId;
  }

  // Рекурсивно обрабатываем дочерние элементы
  if (originalData.components && clonedComponent.components) {
    const children = clonedComponent.components();
    (originalData.components as Record<string, unknown>[]).forEach(
      (childData: Record<string, unknown>, index: number) => {
        const childComponent = children.at(index);
        if (childComponent) {
          buildIdMapping(childData, childComponent, idMapping);
        }
      },
    );
  }

  return idMapping;
}

/**
 * Обрабатывает селектор с применением маппинга ID.
 * Корректно обрабатывает состояния и различные типы селекторов.
 */
export function processSelector(selectors: string[], idMapping: IIdMapping): string {
  if (!Array.isArray(selectors) || selectors.length === 0) {
    return '';
  }

  if (selectors.length === 1 && selectors[0].startsWith('#')) {
    const selector = selectors[0];
    const colonIndex = selector.indexOf(':');

    if (colonIndex > 0) {
      // Селектор содержит состояние (например '#id:hover')
      const originalId = selector.substring(1, colonIndex);
      const state = selector.substring(colonIndex); // Сохраняем состояние включая ':'

      // Применяем маппинг только к базовому ID и восстанавливаем состояние
      const mappedId = idMapping[originalId] || originalId;

      return `#${mappedId}${state}`;
    } else {
      // Обычный ID селектор
      const originalId = selector.substring(1);

      return idMapping[originalId] ? `#${idMapping[originalId]}` : selector;
    }
  } else {
    // Обрабатываем классовые селекторы
    return selectors
      .map((sel: string) => {
        const colonIndex = sel.indexOf(':');
        const cleanSelector = colonIndex > 0 ? sel.substring(0, colonIndex) : sel;

        return cleanSelector.startsWith('.') ? cleanSelector : `.${cleanSelector}`;
      })
      .join('');
  }
}

/**
 * Создает опции для setRule на основе данных стиля.
 * Обрабатывает медиазапросы, at-rule типы и дополнительные селекторы.
 */
export function createSetRuleOptions(styleData: IStyleData): Record<string, unknown> {
  const options: Record<string, unknown> = {
    atRuleType: styleData.atRuleType,
    atRuleParams: styleData.mediaText,
    addStyles: false,
  };

  if (styleData.selectorsAdd) {
    options.selectorsAdd = styleData.selectorsAdd;
  }

  return options;
}

/**
 * Проверяет корректность данных стиля.
 * Валидирует наличие обязательных полей и их типы.
 */
export function isValidStyleData(styleData: Record<string, unknown>): styleData is IStyleData {
  return !!(
    styleData.style &&
    typeof styleData.style === 'object' &&
    Array.isArray(styleData.selectors) &&
    styleData.selectors.length > 0
  );
}

/**
 * Применяет флаги !important к правилу.
 * Обрабатывает как boolean, так и массив строк.
 */
export function applyImportantFlags(rule: IGrapesJSRule, important?: boolean | string[]): void {
  if (important && rule && rule.set) {
    rule.set('important', important);
  }
}

/**
 * Десериализует данные буфера обмена из localStorage.
 */
export function deserializeClipboardData(data: string): IClipboardData | null {
  try {
    return JSON.parse(data);
  } catch (error) {
    console.info('[CrossTabClipboard] Error parsing clipboard data:', error);

    return null;
  }
}

/**
 * Проверяет валидность данных буфера обмена.
 */
export function isValidClipboardData(data: unknown): data is IClipboardData {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const obj = data as Record<string, unknown>;

  return !!(Array.isArray(obj.components) && Array.isArray(obj.styles) && typeof obj.timestamp === 'number');
}
